import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowRight,
  Check,
  Circle,
  Loader2,
  RefreshCw,
  Sparkles,
  X,
  XCircle,
} from "lucide-react";
import { compareAgentVersion } from "../utils/agentVersion";
import { useToast } from "../toast/ToastContext";
import {
  cloudCommand,
  cloudGetAgentReleaseLatest,
  cloudGetProject,
  cloudUpdateAgent,
  cloudWaitCommandResult,
  getCloudToken,
} from "../api/client";
import { formatWriteError } from "../utils/writeGate";

export const MIN_AGENT_VERSION_OTA = "0.2.20";

const UPGRADE_POLL_FAST_MS = 1000;
const UPGRADE_POLL_MS = 2000;
/**
 * 集成重载后版本确认窗口。
 * Agent 写盘后需 async_reload + WS 重连 + hello/heartbeat 才上报新版本；
 * 实测可能超过 90s，过短会导致「包已写入但 UI 判定失败」。
 */
const VERIFY_RELOAD_MS = 300_000;
const VERIFY_AFTER_RESTART_MS = 240_000;
const TOAST_LONG_MS = 8_000;

type StepId = "ssh" | "dispatch" | "host_apply" | "reload" | "verify";
type StepStatus = "wait" | "run" | "done" | "fail";

type OtaStep = {
  id: StepId;
  label: string;
  detail?: string;
  status: StepStatus;
};

type OtaOutcome = {
  ok: boolean;
  title: string;
  detail: string;
};

const STEP_DEFS: Array<{ id: StepId; label: string }> = [
  { id: "ssh", label: "检查并开启 SSH 诊断旁路" },
  { id: "dispatch", label: "下发升级指令" },
  { id: "host_apply", label: "主机下载、校验并写入安装包" },
  { id: "reload", label: "重载 Agent 集成（不自动重启 HA）" },
  { id: "verify", label: "确认 Agent 版本已更新" },
];

function sleep(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function initialSteps(): OtaStep[] {
  return STEP_DEFS.map((s) => ({ ...s, status: "wait" as const }));
}

function otaErrorLabel(code?: string | null): string {
  const c = String(code || "").trim();
  if (!c) return "未知错误";
  if (c.startsWith("download_http_")) {
    return `安装包下载失败（HTTP ${c.replace("download_http_", "")}）`;
  }
  switch (c) {
    case "sha256_mismatch":
      return "安装包校验失败，请重试";
    case "missing_package_url_or_sha256":
      return "云端未配置安装包";
    case "invalid_archive_layout":
      return "安装包格式错误";
    case "extract_failed":
      return "解压失败，请检查 HA 磁盘权限";
    case "config_dir_unavailable":
      return "无法定位 HA 配置目录";
    case "command_timeout":
      return "主机执行超时";
    case "GATE_AGENT_OFFLINE":
    case "agent_offline":
      return "Agent 离线，无法升级";
    case "denied":
      return "主机拒绝执行升级";
    case "service_not_allowed":
      return "主机未开放重启服务";
    default:
      return c;
  }
}

function sessionKey(projectId: string) {
  return `udhub_ota_${projectId}`;
}

function splitStepPresentation(step: OtaStep): {
  meta?: string;
  sub?: string;
} {
  const detail = step.detail?.trim();
  if (!detail) return {};
  if (
    step.id === "verify" &&
    /^(已连接|重连中) · 当前上报/.test(detail)
  ) {
    return { meta: detail };
  }
  return { sub: detail };
}

function progressPercent(steps: OtaStep[]): number {
  if (steps.length === 0) return 0;
  let score = 0;
  for (const s of steps) {
    if (s.status === "done") score += 1;
    else if (s.status === "run") score += 0.45;
    else if (s.status === "fail") score += 0.2;
  }
  return Math.min(100, Math.round((score / steps.length) * 100));
}

function StepGlyph({ status }: { status: StepStatus }) {
  if (status === "done") return <Check size={14} strokeWidth={2.5} />;
  if (status === "fail") return <XCircle size={14} strokeWidth={2.2} />;
  if (status === "run") {
    return <Loader2 size={14} strokeWidth={2.2} className="ota-spin" />;
  }
  return <Circle size={12} strokeWidth={2} />;
}

interface AgentOtaButtonProps {
  projectId: string;
  agent: string;
  hostBound: boolean;
  agentOnline?: boolean;
  cloudReady?: boolean;
  onUpdated?: () => void;
  className?: string;
  label?: string;
  /** hero：嵌在项目名称卡片内；dropdown：按钮下方浮层 */
  placement?: "hero" | "dropdown";
}

export function AgentOtaButton({
  projectId,
  agent,
  hostBound,
  agentOnline = false,
  cloudReady = true,
  onUpdated,
  className = "btn btn-ghost btn-xs",
  label = "系统升级",
  placement = "dropdown",
}: AgentOtaButtonProps) {
  const { toast: tip } = useToast();
  const [busy, setBusy] = useState(false);
  const [latest, setLatest] = useState<string | null>(null);
  const [releaseLoaded, setReleaseLoaded] = useState(false);
  const [steps, setSteps] = useState<OtaStep[]>(initialSteps);
  const [panelOpen, setPanelOpen] = useState(false);
  const [outcome, setOutcome] = useState<OtaOutcome | null>(null);
  const [targetVer, setTargetVer] = useState<string | null>(null);
  const [needRestart, setNeedRestart] = useState(false);
  const pollCancelRef = useRef(false);

  const setStep = (id: StepId, status: StepStatus, detail?: string) => {
    setSteps((prev) =>
      prev.map((s) =>
        s.id === id
          ? { ...s, status, detail: detail ?? s.detail }
          : status === "run" && s.status === "run"
            ? { ...s, status: "wait" }
            : s,
      ),
    );
  };

  const markDoneBefore = (id: StepId) => {
    const idx = STEP_DEFS.findIndex((s) => s.id === id);
    setSteps((prev) =>
      prev.map((s, i) =>
        i < idx && s.status !== "fail" ? { ...s, status: "done" } : s,
      ),
    );
  };

  useEffect(() => {
    pollCancelRef.current = false;
    return () => {
      pollCancelRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (!panelOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPanelOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [panelOpen]);

  useEffect(() => {
    if (!hostBound || !cloudReady) {
      setReleaseLoaded(false);
      setLatest(null);
      return;
    }
    let active = true;
    void cloudGetAgentReleaseLatest()
      .then((r) => {
        if (!active) return;
        setLatest(r.release?.version ?? null);
      })
      .catch(() => {
        if (!active) return;
        setLatest(null);
      })
      .finally(() => {
        if (active) setReleaseLoaded(true);
      });
    return () => {
      active = false;
    };
  }, [hostBound, cloudReady, agent]);

  const failUpgrade = (title: string, detail: string, opts?: { needRestart?: boolean }) => {
    setNeedRestart(Boolean(opts?.needRestart));
    setOutcome({ ok: false, title, detail });
    setPanelOpen(true);
    tip(`${title}：${detail}`, { duration: TOAST_LONG_MS });
  };

  const succeedUpgrade = (version: string) => {
    const title = "系统升级成功";
    const detail = `当前 Agent v${version}（主机已重新上报新版本）`;
    setNeedRestart(false);
    setSteps((prev) =>
      prev.map((s) => ({
        ...s,
        status: "done" as const,
        detail:
          s.id === "reload"
            ? s.detail || "集成已重载"
            : s.id === "verify"
              ? `已确认 v${version}`
              : s.detail,
      })),
    );
    setOutcome({ ok: true, title, detail });
    setPanelOpen(true);
    tip(`${title} · ${detail}`, { duration: TOAST_LONG_MS });
    try {
      sessionStorage.removeItem(sessionKey(projectId));
    } catch {
      /* ignore */
    }
    onUpdated?.();
  };

  const pollUpgradeResult = async (
    targetVersion: string,
    timeoutMs: number,
  ) => {
    markDoneBefore("verify");
    setStep(
      "verify",
      "run",
      `目标 v${targetVersion}，等待版本上报…`,
    );
    const deadline = Date.now() + timeoutMs;
    let attempt = 0;
    while (Date.now() < deadline && !pollCancelRef.current) {
      // 先查再等：首轮立刻探测，前几次 1s，之后 2s
      if (attempt > 0) {
        const delay =
          attempt <= 10 ? UPGRADE_POLL_FAST_MS : UPGRADE_POLL_MS;
        await sleep(delay);
      }
      attempt += 1;
      if (pollCancelRef.current) return false;
      try {
        const { project } = await cloudGetProject(projectId);
        const current = project.agent;
        const online = project.agentStatus === "已连接";
        setStep(
          "verify",
          "run",
          online
            ? `已连接 · 当前上报 v${current || "—"}`
            : `重连中 · 当前上报 v${current || "—"}`,
        );
        if (current && compareAgentVersion(current, targetVersion) >= 0) {
          // Require online: offline/reloading may still show a stale or
          // optimistic DB value from a previous attempt.
          if (!online) {
            setStep(
              "verify",
              "run",
              `版本已见 v${current}，等待 Agent 重新连上…`,
            );
          } else {
            setStep("verify", "done", `已确认 v${current}`);
            succeedUpgrade(current);
            return true;
          }
        }
      } catch {
        setStep("verify", "run", "查询版本中（网络抖动可忽略）…");
      }
    }
    if (!pollCancelRef.current) {
      setStep("verify", "fail", "超时未确认到新版本");
      failUpgrade(
        "版本未刷新",
        `安装包多半已写入，但集成重载后仍上报旧版本（目标 v${targetVersion}）。可点「重启主机」一次性加载新代码，或到 HA「设置 → 系统」手动重启。`,
        { needRestart: true },
      );
      onUpdated?.();
    }
    return false;
  };

  const doRestart = async (target: string) => {
    setNeedRestart(false);
    setBusy(true);
    setOutcome(null);
    setPanelOpen(true);
    tip("正在重启 Home Assistant 以加载新 Agent…", { duration: TOAST_LONG_MS });
    setSteps((prev) =>
      prev.map((s) => {
        if (s.id === "reload") {
          return {
            ...s,
            status: "done" as const,
            detail: s.detail?.includes("重载")
              ? s.detail
              : "安装包已写入；正在重启 HA 加载新代码…",
          };
        }
        if (s.id === "verify") {
          return {
            ...s,
            status: "run" as const,
            detail: "主机重启中，等待 Agent 重新上报版本…",
          };
        }
        return s.status === "fail" ? s : { ...s, status: "done" as const };
      }),
    );
    try {
      const res = (await cloudCommand(projectId, {
        kind: "call_service",
        domain: "homeassistant",
        service: "restart",
      })) as { command_id?: string };
      const commandId = res.command_id;
      if (commandId) {
        const receipt = await cloudWaitCommandResult(projectId, commandId, {
          timeoutMs: 20_000,
          intervalMs: 500,
        });
        if (receipt.status === "failed" || receipt.status === "denied") {
          const err = otaErrorLabel(receipt.error || receipt.status);
          setStep("verify", "fail", err);
          failUpgrade("重启失败", err, { needRestart: true });
          return;
        }
      }
      tip("主机重启中，等待 Agent 重新上线…", { duration: TOAST_LONG_MS });
      await sleep(3_000);
      await pollUpgradeResult(target, VERIFY_AFTER_RESTART_MS);
    } catch (err) {
      const msg = formatWriteError(err, "重启指令下发失败");
      setStep("verify", "fail", msg);
      failUpgrade("重启失败", msg, { needRestart: true });
    } finally {
      setBusy(false);
    }
  };

  const requestHostRestart = () => {
    if (busy) return;
    const target = targetVer || latest;
    if (!target) return;
    if (
      !window.confirm(
        `将重启 Home Assistant 以加载 Agent v${target}。\n\n现场自动化会短暂中断（约 1–2 分钟）。确认继续？`,
      )
    ) {
      tip("已取消重启", { duration: TOAST_LONG_MS });
      return;
    }
    void doRestart(target);
  };

  // 刷新页面后若升级未完成，继续轮询
  useEffect(() => {
    if (!hostBound || !cloudReady || !getCloudToken()) return;
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(sessionKey(projectId));
    } catch {
      return;
    }
    if (!raw) return;
    let saved: { target: string; from?: string; startedAt: number };
    try {
      saved = JSON.parse(raw) as {
        target: string;
        from?: string;
        startedAt: number;
      };
    } catch {
      return;
    }
    if (!saved.target) return;
    if (
      Date.now() - (saved.startedAt || 0) >
      VERIFY_AFTER_RESTART_MS + 60_000
    ) {
      try {
        sessionStorage.removeItem(sessionKey(projectId));
      } catch {
        /* ignore */
      }
      return;
    }
    if (agent && compareAgentVersion(agent, saved.target) >= 0) {
      try {
        sessionStorage.removeItem(sessionKey(projectId));
      } catch {
        /* ignore */
      }
      return;
    }
    setTargetVer(saved.target);
    setPanelOpen(true);
    setBusy(true);
    setNeedRestart(false);
    setSteps(
      STEP_DEFS.map((s) => ({
        ...s,
        status: s.id === "verify" ? ("run" as const) : ("done" as const),
        detail:
          s.id === "verify"
            ? `刷新后继续等待 v${saved.target}…`
            : undefined,
      })),
    );
    tip(`继续检测系统升级进度 · 目标 v${saved.target}`, {
      duration: TOAST_LONG_MS,
    });
    void (async () => {
      try {
        await pollUpgradeResult(saved.target, VERIFY_RELOAD_MS);
      } finally {
        setBusy(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resume once per mount
  }, [projectId, hostBound, cloudReady]);

  if (!hostBound || !cloudReady || !getCloudToken()) return null;
  if (!releaseLoaded || latest == null) return null;

  const supportsOta = compareAgentVersion(agent, MIN_AGENT_VERSION_OTA) >= 0;
  const hasNewVersion = compareAgentVersion(agent, latest) < 0;
  const inFlight = busy || panelOpen || needRestart;

  // 无可升级版本时隐藏入口；升级中 / 进度面板 / 需重启时保留
  if (!inFlight && (!supportsOta || !hasNewVersion)) return null;

  let disabledReason: string | undefined;
  if (!agentOnline) disabledReason = "Agent 未连接";
  else if (!supportsOta) {
    disabledReason = `需 Agent ≥ ${MIN_AGENT_VERSION_OTA} 才支持云端升级`;
  }

  const buttonLabel = busy ? "升级中…" : label;

  const title = [
    `可升级至 v${latest}`,
    agent ? `当前 v${agent}` : null,
    disabledReason,
  ]
    .filter(Boolean)
    .join(" · ");

  const onClick = () => {
    if (busy) return;
    if (disabledReason) {
      tip(disabledReason, { duration: TOAST_LONG_MS });
      return;
    }
    if (!hasNewVersion) {
      tip(`Agent 已是最新 v${latest}，无需升级`, { duration: TOAST_LONG_MS });
      return;
    }
    void (async () => {
      setBusy(true);
      setOutcome(null);
      setNeedRestart(false);
      setPanelOpen(true);
      setSteps(initialSteps());
      setTargetVer(latest);
      tip(`开始系统升级 → v${latest}`, { duration: TOAST_LONG_MS });

      try {
        setStep("ssh", "run", "能开则自动开启 SSH Add-on…");
        tip("① 升级前检查 SSH 旁路…", { duration: TOAST_LONG_MS });
        setStep("dispatch", "run", "正在向主机下发 agent_update…");
        const res = await cloudUpdateAgent(projectId);
        setStep(
          "ssh",
          "done",
          res.ssh_bypass_ensure?.skipped
            ? `已跳过（${res.ssh_bypass_ensure.reason || "—"}）`
            : "已下发检查/开启",
        );
        if (res.skipped) {
          setStep("dispatch", "done", res.message || "已是最新");
          setOutcome({
            ok: true,
            title: "无需升级",
            detail: res.message || "当前已是最新版本",
          });
          tip(res.message || "当前已是最新版本", { duration: TOAST_LONG_MS });
          return;
        }

        const target = res.version || latest;
        const from = res.previous_version || agent;
        const commandId = res.command_id;
        setTargetVer(target);
        setStep(
          "dispatch",
          "done",
          `v${from} → v${target}${commandId ? ` · ${commandId}` : ""}`,
        );
        tip(`① 升级指令已下发 · v${from} → v${target}`, {
          duration: TOAST_LONG_MS,
        });

        try {
          sessionStorage.setItem(
            sessionKey(projectId),
            JSON.stringify({
              target,
              from,
              commandId,
              startedAt: Date.now(),
            }),
          );
        } catch {
          /* ignore */
        }

        if (!commandId) {
          setStep("dispatch", "fail", "未返回 command_id");
          failUpgrade("系统升级失败", "云端未返回命令号，请重试");
          return;
        }

        setStep("host_apply", "run", "等待主机下载并写入…");
        tip("② 主机下载、校验安装包…", { duration: TOAST_LONG_MS });
        const receipt = await cloudWaitCommandResult(projectId, commandId, {
          timeoutMs: 120_000,
          intervalMs: 800,
        });

        if (receipt.status === "failed" || receipt.status === "denied") {
          const err = otaErrorLabel(receipt.error || receipt.status);
          setStep("host_apply", "fail", err);
          failUpgrade("系统升级失败", err);
          try {
            sessionStorage.removeItem(sessionKey(projectId));
          } catch {
            /* ignore */
          }
          return;
        }

        if (receipt.status === "timeout" || receipt.status === "pending") {
          setStep("host_apply", "fail", "主机未在时限内回执");
          failUpgrade(
            "系统升级失败",
            "主机未回执（超时）。请确认 Agent 在线后重试。",
          );
          return;
        }

        if (receipt.status === "ok") {
          setStep("host_apply", "done", "安装包已写入主机");
          // HA 2026.9：仅 async_reload / unload+setup 经常仍跑旧模块；
          // 写盘成功后直接重启 Core，才能让新 Agent 真正生效。
          setStep("reload", "done", "安装包已落盘，改为重启 HA 加载新版本");
          tip("③ 写入成功，正在重启 Home Assistant 以加载新 Agent…", {
            duration: TOAST_LONG_MS,
          });
          await doRestart(target);
          return;
        } else {
          setStep("host_apply", "fail", `未知回执：${receipt.status}`);
          failUpgrade("系统升级失败", `主机回执异常：${receipt.status}`);
        }
      } catch (err) {
        const msg = formatWriteError(err, "请确认 Agent 在线后重试");
        setSteps((prev) => {
          const runIdx = prev.findIndex((s) => s.status === "run");
          if (runIdx < 0) return prev;
          return prev.map((s, i) =>
            i === runIdx ? { ...s, status: "fail" as const, detail: msg } : s,
          );
        });
        failUpgrade("系统升级失败", msg);
        try {
          sessionStorage.removeItem(sessionKey(projectId));
        } catch {
          /* ignore */
        }
      } finally {
        setBusy(false);
      }
    })();
  };

  const showButton = (hasNewVersion && supportsOta) || busy || panelOpen;
  const pct = progressPercent(steps);
  const fromVer = agent && agent !== "—" && agent !== "待同步" ? agent : "—";

  const stage = panelOpen
    ? createPortal(
        <div
          className="ota-stage-backdrop"
          role="presentation"
          onClick={() => {
            if (!busy) setPanelOpen(false);
          }}
        >
          <div
            className={`ota-stage${
              outcome?.ok === false
                ? " is-fail"
                : outcome?.ok
                  ? " is-ok"
                  : ""
            }`}
            role="dialog"
            aria-modal="true"
            aria-label="系统升级"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ota-stage-glow" aria-hidden />
            <header className="ota-stage-head">
              <div className="ota-stage-badge">
                <Sparkles size={14} />
                <span>Agent OTA</span>
              </div>
              <button
                type="button"
                className="icon-btn ota-stage-close"
                onClick={() => setPanelOpen(false)}
                aria-label="关闭升级进度"
              >
                <X size={18} />
              </button>
            </header>

            <div className="ota-stage-hero">
              <h2>
                {outcome
                  ? outcome.title
                  : busy
                    ? "正在升级系统"
                    : targetVer
                      ? "系统升级"
                      : "系统升级"}
              </h2>
              <p className="ota-stage-sub">
                {outcome?.detail
                  ? outcome.detail
                  : targetVer
                    ? "请保持页面打开，主机将自动完成下载、写入与校验"
                    : "准备开始"}
              </p>

              <div className="ota-stage-versions" aria-label="版本变更">
                <span className="ota-ver ota-ver-from">v{fromVer}</span>
                <span className="ota-ver-arrow" aria-hidden>
                  <ArrowRight size={16} />
                </span>
                <span className="ota-ver ota-ver-to">
                  v{targetVer || latest || "—"}
                </span>
              </div>

              <div
                className="ota-stage-meter"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={pct}
              >
                <div
                  className="ota-stage-meter-fill"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="ota-stage-meter-meta">
                <span>{busy ? "进行中" : outcome ? "已结束" : "待命"}</span>
                <span>{pct}%</span>
              </div>
            </div>

            <ol className="ota-timeline">
              {steps.map((step, index) => {
                const present = splitStepPresentation(step);
                const detail = present.meta || present.sub;
                return (
                  <li
                    key={step.id}
                    className="ota-timeline-item"
                    data-status={step.status}
                  >
                    <div className="ota-timeline-rail" aria-hidden>
                      <span className="ota-timeline-dot">
                        <StepGlyph status={step.status} />
                      </span>
                      {index < steps.length - 1 ? (
                        <span className="ota-timeline-line" />
                      ) : null}
                    </div>
                    <div className="ota-timeline-body">
                      <div className="ota-timeline-title-row">
                        <strong>{step.label}</strong>
                        <em>
                          {step.status === "done"
                            ? "完成"
                            : step.status === "run"
                              ? "进行中"
                              : step.status === "fail"
                                ? "失败"
                                : "等待"}
                        </em>
                      </div>
                      {detail ? (
                        <p className="ota-timeline-detail">{detail}</p>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ol>

            {outcome?.ok === false && !busy ? (
              <div className="ota-stage-actions">
                {needRestart ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={requestHostRestart}
                  >
                    <RefreshCw size={16} />
                    重启主机
                  </button>
                ) : null}
                {showButton ? (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={onClick}
                  >
                    重试升级
                  </button>
                ) : null}
              </div>
            ) : null}

            {outcome?.ok && !busy ? (
              <div className="ota-stage-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setPanelOpen(false)}
                >
                  完成
                </button>
              </div>
            ) : null}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div
      className={
        placement === "hero" ? "agent-ota agent-ota--hero" : "agent-ota"
      }
    >
      {showButton ? (
        <button
          className={className}
          type="button"
          disabled={busy || Boolean(disabledReason)}
          title={title}
          onClick={onClick}
        >
          {busy ? (
            <>
              <Loader2 size={14} className="ota-spin" />
              {buttonLabel}
            </>
          ) : (
            buttonLabel
          )}
        </button>
      ) : needRestart ? (
        <button
          className="btn btn-primary btn-xs"
          type="button"
          disabled={busy}
          onClick={requestHostRestart}
        >
          重启主机
        </button>
      ) : null}
      {stage}
    </div>
  );
}
